import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FormSection, FormInput, SingleSelectPicker } from '@/components';
import { ITaskFormValues, TASK_CATEGORY_CONFIG } from '@/types/admin/tasks';
import { ADMIN_COLORS } from '@/constants/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopDropdownSelector } from '@/components/common/admin/desktop/DesktopDropdownSelector';

interface TaskDetailsSectionProps {
  formData: ITaskFormValues;
  errors: Record<string, string | undefined>;
  onFieldChange: (field: keyof ITaskFormValues, value: any) => void;
}

export const TaskDetailsSection: React.FC<TaskDetailsSectionProps> = ({
  formData,
  errors,
  onFieldChange,
}) => {
  const { layoutMode } = useResponsive();
  const categoryOptions = Object.values(TASK_CATEGORY_CONFIG).map(category => ({
    label: category.label,
    value: category.key,
  }));

  return (
    <FormSection title="Task Details" subtitle="Basic information about the task">
      <View style={styles.fieldContainer}>
        <FormInput
          label="Task Name *"
          value={formData.title}
          onChangeText={(text: string) => onFieldChange('title', text)}
          placeholder="Enter task name"
          error={errors.title}
          maxLength={100}
          autoCapitalize="sentences"
          returnKeyType="next"
        />
      </View>

      <View style={styles.fieldContainer}>
        {layoutMode === 'desktop' ? (
          <DesktopDropdownSelector
            label="Category"
            selectedValue={formData.category}
            options={categoryOptions}
            onChange={(value) => onFieldChange('category', value)}
            placeholder="Select category"
            error={errors.category}
            required
          />
        ) : (
          <SingleSelectPicker
            label="Category"
            selectedValue={formData.category}
            onSelectionChange={(value: string) => onFieldChange('category', value)}
            options={categoryOptions}
            placeholder="Select category"
            error={errors.category}
            required
          />
        )}
      </View>

      <View style={styles.fieldContainer}>
        <FormInput
          label="Location"
          value={formData.location || ''}
          onChangeText={(text: string) => onFieldChange('location', text)}
          placeholder="Enter location (optional)"
          error={errors.location}
          maxLength={100}
          autoCapitalize="words"
          returnKeyType="next"
        />
      </View>

      <View style={styles.fieldContainer}>
        <FormInput
          label="Description"
          value={formData.description || ''}
          onChangeText={(text: string) => onFieldChange('description', text)}
          placeholder="Enter task description (optional)"
          error={errors.description}
          maxLength={1000}
          multiline
          numberOfLines={4}
          autoCapitalize="sentences"
        />
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  fieldContainer: {
    marginBottom: 16,
  },
});
